import React, { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

import styles from "./bottomNavBar.module.scss";
import { StateContext } from "../../../context/globalContext/context";
import { iGlobalContext } from "../../../context/globalContext/interface";
import { iSocketContext, useSocketContext } from "../../../context/socketContext/SocketContext";
import { useRecommendedTripContext } from "../../../context/recommendedTripContext/recommendedTripContext";
import { iRecommendedTripContext } from "../../../context/recommendedTripContext/recommendedTripContextInterface";
import { ROUTES } from "../../../constant";
import { APP_CONFIG } from "../../../constant/common";
import { SELECTED_CHAT_DATA } from "../../../constant/dummyData";
import { GA_EVENTS, GA_LABEL_ENUMS, triggerGAEvent } from "../../../constant/google-analytics/GAEvents";
import { useNavigationUrl } from "../../../hooks/useNavigationUrl";
import { usePixelEvent } from "../../../hooks/usePixelEvent";
import { useResetState } from "../../../utils/resetBookingData";
import { resolveBoolFlag } from "../../../utils/appConfig/resolveBoolFlag";
import { rotateChatId } from "../../../utils/chat/rotateChatId";
import { clearChatBookingOrigin } from "../../../utils/booking/clearChatBookingOrigin";
import { logBookConversion } from "../../../experiments";

import { prefetchBookingChunk } from "../../../utils/prefetch/prefetchBookingChunk";
import { isAppEmbedFlow } from "../../../utils/embedPortalBridge";
import { brandLabel } from "../../../utils/partnerBranding";
import { useHistoryContext } from "../../../context/historyContext/historyContext";
import { iHistoryContext } from "../../../context/historyContext/interface";
import { ScreenTypes } from "../../../enums";
import { fsPx } from "../../../styles/design-tokens";

export default function BottomNavBar(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  const {
    userInfo,
    appConfig,
    branding,
    setChatData,
    setChatInputText,
    setHasTriggeredPreBooking,
    setenableMultiSelect,
    setSelectedFlightOptions,
    setActivityData,
    setActivitySearchParams,
    setActivityCartItems,
    setIsActivityReviewPage,
    setShowActivityCollapsedHeader,
    setHideDetails,
    setRecentTravelersList,
    setHotelTravelersList,
    setGuestList,
    setAirPorts,
    setFlightData,
    setHotelData,
    setBookingDetails,
    setSelectedHotelRoomArr,
    setSelectedChatData,
    setBookClickBeforeLoginFlag,
    setFirstScreen,
    setScreen,
    setIsBookingSearchForm,
  }: iGlobalContext = useContext(StateContext);

  const { navigateWithParams } = useNavigationUrl();
  const { sendPixelEvent } = usePixelEvent();
  const resetState = useResetState();
  const { resetTranscript } = useSpeechRecognition();
  const { handleCloseSocketConnection }: iSocketContext = useSocketContext();
  const { handleCancel, setDisableInput }: iRecommendedTripContext = useRecommendedTripContext();
  const { setCurrentHistoryId }: iHistoryContext = useHistoryContext();
  // Running inside the native mobile app (WebView, ticket login).
  const nativeApp = isAppEmbedFlow();

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      window.scrollTo(0, 0);
    });
  };

  const handleChat = async () => {
    SpeechRecognition.stopListening();
    resetTranscript();
    triggerGAEvent(GA_LABEL_ENUMS.new_trip_click);
    try {
      // Cancel BEFORE clearing: handleCancel (recommendedTrip handleCancelStreaming)
      // always appends a "Search interrupted / Regenerate" bubble via a functional
      // update — after setChatData([]) it left a chat holding just that bubble.
      // Same order as the sidebar's working new-chat (useCustomSideBarLogic.handleNav).
      handleCloseSocketConnection();
      handleCancel();
      setChatData([]);
      setDisableInput(false);
      setChatInputText("");
      setHasTriggeredPreBooking(false);
      rotateChatId();
      setSelectedChatData({ ...SELECTED_CHAT_DATA });
      await resetState();
      setenableMultiSelect(false);
      resetState();
      setSelectedFlightOptions({ flights: false, hotels: false, activities: false });
      setActivityData(null);
      setActivitySearchParams(null);
      setActivityCartItems([]);
      setIsActivityReviewPage(false);
      setShowActivityCollapsedHeader(false);
      setHideDetails(false);
      try {
        sessionStorage.removeItem("activity_booking_flow_state");
        sessionStorage.removeItem("activity_review_state");
        sessionStorage.removeItem("activity_cart_items");
      } catch {}
      if (nativeApp) {
        // Native app: "new chat" is the same /portal/chat surface that's already
        // mounted, so navigating there is a no-op (no remount, no landing page).
        // Reset in place instead: fresh conversation (greeting + starter chips),
        // no open panel, and a new history row for the next message.
        setCurrentHistoryId("");
        setScreen("");
        setFirstScreen(ScreenTypes.topDestination);
        scrollToTop();
        return;
      }
      setTimeout(() => {
        navigateWithParams(ROUTES.NewTrip);
        scrollToTop();
      }, 100);
    } catch (error) {
      console.error("Error during navigation:", error);
    }
  };

  const handleShop = () => {
    logBookConversion("mobile-nav");
    triggerGAEvent(GA_LABEL_ENUMS.booking_form_stays_view);
    triggerGAEvent(GA_LABEL_ENUMS.book_without_itinerary_click);
    sendPixelEvent("interactedWithChat", { event: GA_EVENTS[GA_LABEL_ENUMS.book_without_itinerary_click]?.label });
    sendPixelEvent("startChat", { event: GA_EVENTS[GA_LABEL_ENUMS.book_without_itinerary_click]?.label });
    setRecentTravelersList([]);
    setHotelTravelersList([]);
    setGuestList([]);
    setAirPorts([]);
    setFlightData(null);
    setHotelData(null);
    setHasTriggeredPreBooking(false);
    setActivityCartItems([]);
    setActivityData(null);
    setActivitySearchParams(null);
    try {
      sessionStorage.removeItem("activity_booking_flow_state");
      sessionStorage.removeItem("activity_review_state");
      sessionStorage.removeItem("activity_cart_items");
    } catch {}
    clearChatBookingOrigin();
    setSelectedFlightOptions({ flights: false, hotels: true, activities: false });
    setBookingDetails((prevData) => ({
      ...prevData,
      selectedFlightId: "",
      flightPreferences: {
        connections: null,
        airlines: [],
        refundable: null,
        departureTime: [],
      },
      hotelPreferences: {
        refundable: null,
        hotelChains: [],
        accommodationTypes: ["hotel"],
        userRatings: null,
        starRatings: [],
      },
      roomInfo: [
        {
          roomNo: 1,
          adults: 2,
          child: 0,
          childAges: "",
        },
      ],
    }));
    setSelectedHotelRoomArr((prev) =>
      prev.map((hotel) => ({
        ...hotel,
        selectedHotelRoom: null,
      }))
    );
    setSelectedChatData(SELECTED_CHAT_DATA);
    resetState();
    if (resolveBoolFlag(appConfig, APP_CONFIG.MIDDLE_PANEL_ON)) {
      setFirstScreen("");
    }
    setIsBookingSearchForm(true);
    navigateWithParams(ROUTES.BookingInProgress);
    setBookClickBeforeLoginFlag(true);
    scrollToTop();
    setTimeout(() => {
      setHideDetails(false);
    }, 200);
  };

  // Native app: Trips stays on the in-app surface (/portal/trips via the route
  // rewrite) — a raw /mytrips would leave it and bounce through the web login.
  const handleTrips = () => {
    triggerGAEvent(GA_LABEL_ENUMS.My_trips_click);
    if (nativeApp) navigateWithParams(ROUTES.MyTrips);
    else navigate(ROUTES.MyTrips);
    scrollToTop();
  };

  const handleAccount = () => {
    triggerGAEvent(GA_LABEL_ENUMS.sidebar_profile_click);
    navigateWithParams(ROUTES.Account);
    scrollToTop();
  };

  const isChatActive =
    pathname === ROUTES.Home ||
    pathname.startsWith(ROUTES.TopDestination) ||
    pathname.startsWith(ROUTES.NewTrip) ||
    pathname.startsWith(ROUTES.PortalChat);
  const isTripsActive = pathname.startsWith(ROUTES.MyTrips) || pathname.startsWith(ROUTES.PortalTrips);
  const isShopActive = pathname.startsWith(ROUTES.BookingInProgress) || pathname.startsWith(ROUTES.PortalShop);
  const isAccountActive = pathname.startsWith(ROUTES.Account);

  const initial = userInfo?.name?.[0]?.toUpperCase() ?? "U";

  useEffect(() => {
    const w = window as any;
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(prefetchBookingChunk)
      : window.setTimeout(prefetchBookingChunk, 1200);
    return () => {
      if (w.requestIdleCallback && w.cancelIdleCallback) w.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, []);

  return (
    <nav className={styles.bar} aria-label="Primary">
      <button
        type="button"
        className={`${styles.tab} ${isChatActive ? styles.selected : ""}`}
        onClick={handleChat}
        aria-label={brandLabel(branding, "navChat")}
      >
        <ChatBubbleOutlineOutlinedIcon color="inherit" sx={{ fontSize: fsPx(28) }} />
        <span className={styles.label}>{brandLabel(branding, "navChat")}</span>
      </button>

      <button
        type="button"
        className={`${styles.tab} ${isShopActive ? styles.selected : ""}`}
        onClick={handleShop}
        onMouseEnter={prefetchBookingChunk}
        onTouchStart={prefetchBookingChunk}
        aria-label={brandLabel(branding, "navShop")}
      >
        <ShoppingCartOutlinedIcon color="inherit" sx={{ fontSize: fsPx(28) }} />
        <span className={styles.label}>{brandLabel(branding, "navShop")}</span>
      </button>

      <button
        type="button"
        className={`${styles.tab} ${isTripsActive ? styles.selected : ""}`}
        onClick={handleTrips}
        aria-label={brandLabel(branding, "navTrips")}
      >
        <BookmarkBorderOutlinedIcon color="inherit" sx={{ fontSize: fsPx(28) }} />
        <span className={styles.label}>{brandLabel(branding, "navTrips")}</span>
      </button>

      {/* Native app: the app shell owns account/login — Chat / Shop / Trips only. */}
      {!nativeApp && (
        <button
          type="button"
          className={`${styles.tab} ${styles.youTab} ${isAccountActive ? styles.selected : ""}`}
          onClick={handleAccount}
          aria-label={brandLabel(branding, "navAccount")}
        >
          {userInfo?.profile_pic ? (
            <img
              src={userInfo.profile_pic}
              alt=""
              className={styles.avatarImg}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <span className={styles.avatar}>{initial}</span>
          )}
          <span className={styles.label}>{brandLabel(branding, "navAccount")}</span>
        </button>
      )}
    </nav>
  );
}
